import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { MoveUpRight, ScanLine } from "lucide-react";
import { PRODUCTS } from "@/data/products";
import { Reveal, SectionTag } from "./Reveal";

const ProductCard = ({ product, index, onEnquire }) => {
  const ref = useRef(null);
  const [hover, setHover] = useState(false);
  const rx = useSpring(useMotionValue(0), { stiffness: 120, damping: 18 });
  const ry = useSpring(useMotionValue(0), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * 9);
    rx.set(-py * 9);
  };
  const reset = () => {
    rx.set(0);
    ry.set(0);
    setHover(false);
  };

  return (
    <Reveal delay={index * 0.12}>
      <motion.article
        ref={ref}
        onMouseMove={onMove}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={reset}
        style={{ rotateX: rx, rotateY: ry, transformPerspective: 1000 }}
        className="group relative bg-[#141414] border border-white/10 overflow-hidden"
        data-testid={`product-card-${product.id}`}
      >
        {/* image / wireframe reveal */}
        <div className="relative aspect-[4/3] overflow-hidden bg-black">
          <img
            src={product.images.photo}
            alt={product.name}
            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-500"
            style={{ opacity: hover ? 0 : 1 }}
          />
          <img
            src={product.images.wireframe}
            alt={`${product.name} CAD wireframe`}
            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-500"
            style={{ opacity: hover ? 1 : 0 }}
            data-testid={`product-wireframe-${product.id}`}
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#141414] via-transparent to-transparent" />

          {/* CAD view badge */}
          <div
            className="absolute top-4 left-4 flex items-center gap-2 bg-[#e31e24] px-3 py-1.5 transition-all duration-400"
            style={{
              opacity: hover ? 1 : 0,
              transform: hover ? "translateY(0)" : "translateY(-8px)",
            }}
          >
            <ScanLine size={13} className="text-white" />
            <span className="font-mono-rp text-[10px] tracking-[0.2em] uppercase text-white">
              CAD View
            </span>
          </div>

          {/* price chip */}
          <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-sm border border-white/15 px-3 py-1.5">
            <span
              className="font-display text-lg text-white leading-none"
              data-testid={`product-price-${product.id}`}
            >
              {product.priceOnEnquiry
                ? "P.O.A."
                : `${product.price.display} `}
              {!product.priceOnEnquiry && (
                <span className="text-white/50 text-xs align-top">
                  {product.price.currency}
                </span>
              )}
            </span>
          </div>
        </div>

        {/* body */}
        <div className="p-6 md:p-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="font-mono-rp text-[10px] tracking-[0.25em] uppercase text-[#e31e24]">
              {product.sku}
            </span>
            <span className="h-px flex-1 bg-white/10" />
            <span className="font-mono-rp text-[10px] tracking-[0.15em] uppercase text-white/40">
              {product.chassis}
            </span>
          </div>

          <h3 className="font-display text-3xl md:text-4xl text-white mb-3">
            {product.name}
          </h3>
          <p className="text-white/55 text-sm mb-6">{product.tagline}</p>

          <ul className="space-y-2 mb-7">
            {product.specs.map((s) => (
              <li
                key={s}
                className="flex items-start gap-3 text-white/70 text-sm"
              >
                <span className="mt-1.5 h-1.5 w-1.5 bg-[#e31e24] rotate-45 shrink-0" />
                {s}
              </li>
            ))}
          </ul>

          <button
            onClick={() => onEnquire(product.name)}
            className="btn-race w-full justify-center"
            data-testid={`enquire-btn-${product.id}`}
          >
            {product.cta} <MoveUpRight size={17} />
          </button>
          <p className="mt-3 font-mono-rp text-[11px] text-white/50 text-center">
            {product.shippingNote}
          </p>
        </div>
      </motion.article>
    </Reveal>
  );
};

export const Products = ({ onEnquire }) => (
  <section
    id="parts"
    className="relative py-24 md:py-36 px-5 md:px-10"
    data-testid="products-section"
  >
    <div className="mx-auto max-w-[1400px]">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <SectionTag index="01" label="The Parts" />
          <h2 className="font-display text-[clamp(2.6rem,7vw,6rem)] text-white max-w-2xl">
            Built for one <span className="text-[#e31e24]">car</span>, not every
            car.
          </h2>
        </div>
        <Reveal delay={0.15}>
          <p className="text-white/55 text-sm md:text-base max-w-xs md:text-right">
            More parts coming. Each one modelled, printed and test-fitted before
            it ever ships.
          </p>
        </Reveal>
      </div>

      <div className="grid md:grid-cols-2 gap-6 md:gap-8">
        {PRODUCTS.map((p, i) => (
          <ProductCard
            key={p.id}
            product={p}
            index={i}
            onEnquire={onEnquire}
          />
        ))}
      </div>
    </div>
  </section>
);
